import type { ResidentDocumentGroup } from '@/features/resident/types/resident'
import type {
  ResidentApplicationData,
  ResidentDefaultFormValues,
  ResidentDetailResponse,
} from '@/features/resident/types/resident'
import { getResidentDocumentsForType } from '@/features/resident/utils/residentDocuments'
import { buildAdjustmentDataFromForm, getCalculationTabSnapshot } from '@/features/resident/utils/residentPayload'
import type { LibertyDocumentRow } from '@/features/resident/types/resident'
import type { OldStatementRow } from '@/features/resident/components/detail/LibertyInternalDocumentsSection'
import type { UseFormReturn } from 'react-hook-form'
import type {
  ApplicationDetail,
  ReimbursementDetail,
  ReimbursementDetailDocumentItem,
  ReimbursementDetailMessage,
  ReimbursementDocumentHistoryItem,
  RentalStatusList,
} from '../types/domain'
import type { ReimbursementsDetailInput } from '../types/schemas'
import type { DetailClaimActionFlags, DetailTabVisibility } from '../types'
import { canShowCancelReimbursementButton } from '@/features/resident/utils/residentValidation'
import { allowsCalculationTab } from './statusTransitions'
import { isCompanyPropertyRole, isFieldLockedByStatus } from './permissions'
import { deriveReimbursementReviewStatus } from './detailReviewStatus'

export function buildRentalStatusOptions(rentalList: RentalStatusList | null) {
  const map = rentalList?.rental_status
  if (!map || typeof map !== 'object') return []
  return Object.entries(map).map(([value, status]) => ({
    value,
    label: status?.name ?? value,
  }))
}

export function buildProgressOptions(rentalList: RentalStatusList | null, rentalStatusValue: string) {
  const status = rentalList?.rental_status?.[rentalStatusValue]
  const progress = status?.progress
  if (!progress || typeof progress !== 'object') return []
  return Object.entries(progress).map(([value, label]) => ({ value, label }))
}

export function buildReasonOptions(rentalList: RentalStatusList | null) {
  const map = rentalList?.reason
  if (!map || typeof map !== 'object') return []
  return Object.entries(map).map(([value, label]) => ({ value, label }))
}

export function buildReimbursementRate(
  application: ApplicationDetail | null,
  applicantId?: string,
) {
  const currentApplicant =
    applicantId && application?.co_resident_application
      ? application.co_resident_application[applicantId] ?? null
      : null
  const currentRate = currentApplicant?.rent_amount
  if (currentRate !== undefined && currentRate !== null && String(currentRate) !== '') {
    return currentRate
  }
  return application?.rent_amount
}

export function isContractStarted(application: ApplicationDetail | null): boolean {
  const raw = application?.contract_start_date
  if (!raw) return false
  const t = new Date(String(raw)).getTime()
  return Number.isFinite(t) && t < Date.now()
}

export function isPolicyActivationPending(application: ApplicationDetail | null): boolean {
  return (
    String((application as Record<string, unknown> | null)?.['progress_status'] ?? '') ===
    'Policy Activation Pending'
  )
}

export function buildDocumentEntries(
  detail: ReimbursementDetail | null,
): Array<[string, ReimbursementDetailDocumentItem[]]> {
  const docs = detail?.document
  if (!docs || typeof docs !== 'object') return []
  return Object.entries(docs).filter(
    ([, items]) => Array.isArray(items),
  ) as Array<[string, ReimbursementDetailDocumentItem[]]>
}

export function buildOtherDocumentEntries(
  documentEntries: Array<[string, ReimbursementDetailDocumentItem[]]>,
) {
  return documentEntries.filter(
    ([type]) => !['lease_agreement', 'payment_ledger', 'unlawful_detainer', 'skip_claim_affidavit'].includes(type),
  )
}

export function buildReimbursementAuditFlags(detail: ReimbursementDetail | null) {
  const rd = detail?.resident_default
  if (!rd) {
    return { hasRentalStatusDate: false, hasFirstMonthNotPaid: false, hasAdjustedNotice: false }
  }
  return {
    hasRentalStatusDate: Boolean(String(rd.rental_status_date ?? '').trim()),
    hasFirstMonthNotPaid:
      Boolean(String(rd.first_month_not_paid ?? '').trim()) ||
      (Boolean(String(rd.first_month_not_paid_month ?? '').trim()) &&
        Boolean(String(rd.first_month_not_paid_year ?? '').trim())),
    hasAdjustedNotice: Boolean(String(rd.adjusted_notice ?? '').trim()),
  }
}

export function buildCalculationPayload(
  form: UseFormReturn<ReimbursementsDetailInput>,
  reimbursementRate: number | string | undefined,
  detail?: ReimbursementDetail | null,
  application?: ApplicationDetail | null,
) {
  const values = form.getValues()
  // Angular passes full `defaultProcessData` as amountData / adjustment_data.
  const snapshot =
    detail != null
      ? getCalculationTabSnapshot(
          detail as unknown as ResidentDetailResponse,
          (application ?? null) as unknown as ResidentApplicationData,
          values as unknown as ResidentDefaultFormValues,
        )
      : null

  return buildAdjustmentDataFromForm({
    snapshot,
    formValues: values as unknown as ResidentDefaultFormValues,
    rentAmount: reimbursementRate ?? snapshot?.rent_amount ?? application?.rent_amount,
  })
}

/** Angular `amountData.net_due` — seed Configure Payment amount / Default Net Due. */
export function resolveConfigurePaymentInitialAmount(
  calculationPayload: Record<string, unknown> | null | undefined,
  fallbackNetAmount?: unknown,
): string {
  const netDue = calculationPayload?.net_due
  if (netDue !== undefined && netDue !== null && String(netDue).trim() !== '') {
    return String(netDue)
  }
  if (
    fallbackNetAmount !== undefined &&
    fallbackNetAmount !== null &&
    String(fallbackNetAmount).trim() !== ''
  ) {
    return String(fallbackNetAmount)
  }
  return ''
}

export function mergeInsuranceMessages(detail: ReimbursementDetail | null): ReimbursementDetailMessage[] {
  const primary = Array.isArray(detail?.message_insurance) ? detail.message_insurance : []
  const fallback = Array.isArray(detail?.insurance_message) ? detail.insurance_message : []
  return [...primary, ...fallback]
}

export function buildLibertyDocumentRows(detail: ReimbursementDetail | null): LibertyDocumentRow[] {
  const raw = detail?.liberty_documents
  if (!raw) return []
  const list = Array.isArray(raw) ? raw : Object.values(raw).filter(Boolean)
  if (!list.length) return []
  return list.map((doc, i) => {
    const d = doc as Record<string, unknown>
    const id = d.id ?? d.attachment_id ?? d.note_id ?? i
    return {
      id: id as string | number,
      attachment_path: String(d.attachment_path ?? d.filepath ?? ''),
      attachment_name: String(d.attachment_name ?? d.filename ?? 'document'),
    }
  })
}

export function buildReimbursementStatementRows(detail: ReimbursementDetail | null): OldStatementRow[] {
  const top = detail?.statement_documents
  if (Array.isArray(top) && top.length > 0) {
    return top.map((doc) => {
      const d = doc as Record<string, unknown>
      return {
        attachment_id: (doc.id ?? d.attachment_id) as string | number | undefined,
        filepath: String(doc.filepath ?? d.filepath ?? ''),
        filename: String(doc.filename ?? d.filename ?? ''),
      }
    })
  }
  const raw = detail?.document?.old_statement as
    | OldStatementRow[]
    | Record<string, OldStatementRow>
    | undefined
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  return Object.values(raw).filter(Boolean) as OldStatementRow[]
}

export function buildReimbursementDocumentHistory(
  detail: ReimbursementDetail | null,
): ReimbursementDocumentHistoryItem[] {
  const history = detail?.documentHistory
  if (!Array.isArray(history)) return []
  return history.map((row) => {
    const filePath = String(
      row.file_path ?? row.filepath ?? row.attachment_path ?? row.url ?? row.path ?? '',
    ).trim()
    const attachmentId = row.attachment_id ?? row.id ?? row.file_id
    return {
      ...row,
      attachment_id: attachmentId as string | number | undefined,
      file_path: filePath || undefined,
      filepath: filePath || undefined,
    }
  })
}

export function buildDetailTabVisibility(
  detail: ReimbursementDetail | null,
  isLibertyRole: boolean,
  roleName: string,
  hasPolicy: (codes: string[]) => boolean,
): DetailTabVisibility {
  const showReimbursementTabs = ['SKIPPED', 'EVICTION'].includes(
    detail?.resident_default?.rental_status || '',
  )
  // Angular resident-edit-new: `authService.hasPolicy(['yardi_config']) && residentDetail?.is_pms_property`
  const isPmsProperty = Boolean(detail?.is_pms_property)
  return {
    showReimbursementTabs,
    showCalculationTab: Boolean(
      showReimbursementTabs &&
        isLibertyRole &&
        !['ROLE_CUSTOMER_SERVICE', 'ROLE_CUSTOMER_SERVICE_MANAGER', 'ROLE_REFUND_APPROVAL'].includes(
          roleName,
        ) &&
        allowsCalculationTab(detail?.resident_default?.rental_status || ''),
    ),
    showLibertyInternalTab: hasPolicy(['resident_libertyinternal_view']),
    showPolicyTab:
      (detail?.policy as { default_policy?: string } | undefined)?.default_policy === 'COMPANY',
    showPmsIntegrationTab: isPmsProperty && hasPolicy(['yardi_config']),
  }
}

export function buildClaimActionFlags(
  detail: ReimbursementDetail | null,
  isLibertyRole: boolean,
  roleName: string,
  rentalStatusValue: string,
  showReimbursementTabs: boolean,
  hasLibertyInternalView = false,
): DetailClaimActionFlags {
  const rentalStatus = String(detail?.resident_default?.rental_status ?? '')
  const claimStatus = String(detail?.resident_default?.claim_status ?? '')
  const reviewDecision = String(detail?.resident_default?.review_decision ?? '')
  const rentalProgress = String(detail?.resident_default?.rental_progress ?? '')
  const paymentReviewLog = detail?.payment_review_log ?? []
  const hasPaymentReviewLog = paymentReviewLog.length > 0

  // Angular Configure Payment on calculation tab (liberty + libertyinternal policy + log/status).
  const canShowConfigurePayment =
    isLibertyRole &&
    hasLibertyInternalView &&
    ((!hasPaymentReviewLog &&
      rentalProgress !== 'COMPLETED' &&
      !['DENIED', 'UNDER_REVIEW'].includes(claimStatus)) ||
      (hasPaymentReviewLog &&
        (['L1 Recalculated', 'L2 Recalculated'].includes(reviewDecision) ||
          claimStatus === 'RE_OPENED')))

  return {
    isClaimLockedForUser:
      !isLibertyRole && String(detail?.resident_default?.claim_lock ?? '') === 'Locked',
    isDeniedOrFinalForCompanyProperty:
      isCompanyPropertyRole(roleName) &&
      isFieldLockedByStatus(
        roleName,
        '',
        claimStatus,
        String(detail?.resident_default?.is_final_ledger ?? ''),
      ),
    canShowClaimActions: showReimbursementTabs,
    canCancelReimbursement: canShowCancelReimbursementButton(roleName, detail?.resident_default),
    canReviewDocs:
      isLibertyRole &&
      !['ROLE_CUSTOMER_SERVICE', 'ROLE_CUSTOMER_SERVICE_MANAGER'].includes(roleName) &&
      !['NOT_STARTED', 'ACTIVE', 'CLOSED'].includes(rentalStatus),
    canShowConfigurePayment,
    canShowCalculationSave:
      Boolean(rentalStatus) && !['NOT_STARTED', 'ACTIVE'].includes(rentalStatus),
    calculationSaveDisabled:
      Boolean(reviewDecision) &&
      !['L1 Recalculated', 'L2 Recalculated', 'L2 Approval', 'L3 Approval'].includes(
        reviewDecision,
      ),
    canShowResidentInfoSave: Boolean(String(rentalStatusValue ?? '').trim()),
    canDenyClaim: !['DENIED', 'UNDER_REVIEW', 'LIBERTY_REVIEW', 'EXTERNAL_REVIEW'].includes(
      claimStatus,
    ),
    canReopenClaim: claimStatus === 'DENIED',
  }
}

export function getReimbursementReviewStatus(detail: ReimbursementDetail | null): string {
  return detail ? deriveReimbursementReviewStatus(detail) : 'Not Started'
}

export function getClaimDocumentUploadRows(detail: ReimbursementDetail | null) {
  const claimDocuments = detail?.document as ResidentDocumentGroup | undefined
  return {
    leaseAgreementUploadRows: getResidentDocumentsForType(claimDocuments, 'lease_agreement'),
    paymentLedgerUploadRows: getResidentDocumentsForType(claimDocuments, 'payment_ledger'),
    unlawfulDetainerUploadRows: getResidentDocumentsForType(claimDocuments, 'unlawful_detainer'),
    skipClaimAffidavitUploadRows: getResidentDocumentsForType(claimDocuments, 'skip_claim_affidavit'),
  }
}
